
import numbro from 'numbro'
import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import StageFeedbackDialog from '@/components/StageFeedbackDialog'
import { formatFeedbackTime } from '@/utils/stageFeedback'
import { DELIVERABLE_KINDS, MAX_SPREADSHEETS_PER_UPLOAD, classifyDeliverable, deliverableCount, isSpreadsheetItem, preassignDeliverables } from '@/utils/stageDeliverables'

export default {
  data () {
    return {
      currentFile: null,
      currentStage: null,
      uploadCount: null,
      uploadCancelled: false,
      uploadDialog: false,
      assignDialog: false,
      pendingUploads: [],
      skippedFiles: [],
      viewReportDialog: false,
      viewReportDialogUrl: '',
      viewReportTitle: '',
      feedbackDialog: false,
      feedbackStageName: null,
      feedbackReportVersion: null,
      feedbackCollapsed: false,
      stages: []
    }
  },
  methods: {
    ...mapActions('manager', [
      'updateModelStages'
    ]),
    ...mapActions('storage', [
      'uploadFile',
      'resetUploadFileProgress',
      'getFileDownloadUrl'
    ]),
    ...mapActions('clients', [
      'updateModelStageStatus',
      'loadClientStages',
      'loadClientStageFeedback',
      'logStageFileUpload'
    ]),
    formatFeedbackTime: formatFeedbackTime,
    deliverableLines (stage) {
      // Only the lines that already have versions are shown
      return Object.keys(DELIVERABLE_KINDS).map(kind => {
        const config = DELIVERABLE_KINDS[kind]
        return {
          kind: kind,
          config: config,
          urls: stage[config.urlsKey] || [],
          dates: stage[config.datesKey],
          names: stage[config.namesKey]
        }
      }).filter(line => line.urls.length > 0)
    },
    // The stage right before it: its deliverables are what a Review stage reviews. Names don't
    // always pair ("Comparison Report" is reviewed in "Comparison Review"), the order does
    sourceStageOf (stage) {
      const index = _.findIndex(this.stages, { name: stage.name })
      return index > 0 ? this.stages[index - 1] : null
    },
    isReviewStage (stage) {
      return /Review$/.test(stage.name) && !!this.sourceStageOf(stage)
    },
    // The enabled Review stage that reviews this stage's deliverables, if there is one
    reviewStageOf (stage) {
      const index = _.findIndex(this.stages, { name: stage.name })
      const next = index >= 0 ? this.stages[index + 1] : null
      return next && this.isReviewStage(next) && _.some(this.enabledStages, { name: next.name }) ? next : null
    },
    canSendFeedback (stage) {
      const source = this.sourceStageOf(stage)
      return !!source && deliverableCount(source) > 0
    },
    messagesOf (stage) {
      return this.feedbackByStage[stage.name] || []
    },
    participantsOf (stage) {
      return this.participantsByStage[stage.name] || []
    },
    openFeedback (stage, reportVersion, collapsed) {
      this.feedbackStageName = stage.name
      this.feedbackReportVersion = reportVersion || null
      this.feedbackCollapsed = !!collapsed
      this.feedbackDialog = true
    },
    deliverableName (stageName, version, kind) {
      const label = kind ? ` - ${this.kindLabel(kind)}` : ''
      return `${this.currentClientObject.name} - ${stageName}${label} - v${version}`
    },
    kindConfig (kind) {
      return DELIVERABLE_KINDS[kind]
    },
    kindLabel (kind) {
      return this.$t(`views.ModelingProcess.deliverableKinds.${DELIVERABLE_KINDS[kind].labelKey}`)
    },
    kindItems (kinds) {
      return kinds.map(kind => ({ value: kind, text: this.kindLabel(kind) }))
    },
    openDeliverable (stage, kind, url, fileIndex) {
      const reviewStage = kind === 'report' ? this.reviewStageOf(stage) : null
      if (reviewStage) {
        // Same view as Send Feedback, on the clicked version, with the feedback hidden until asked for
        this.openFeedback(reviewStage, fileIndex + 1, true)
      } else if (kind === 'report') {
        this.viewReportDialogUrl = url
        this.viewReportDialog = true
        this.viewReportTitle = this.deliverableName(stage.name, fileIndex + 1)
      } else {
        // Spreadsheets are stored as attachments, so following the link downloads them
        this.$refs.fileDownload.href = url
        this.$refs.fileDownload.click()
      }
    },
    deleteFile (stageName, kind, fileIndex) {
      if (confirm(this.$t('views.ModelingProcess.deleteFileConfirm'))) {
        const config = DELIVERABLE_KINDS[kind]
        let stages = _.cloneDeep(this.currentClientStages)
        const stageIndex = _.findIndex(this.currentClientObject.stages, { name: stageName })
        let stage = stages[stageIndex]
        const url = stage[config.urlsKey][fileIndex]
        if (stage[config.datesKey]) {
          delete stage[config.datesKey][url]
        }
        if (stage[config.namesKey]) {
          delete stage[config.namesKey][url]
        }
        stage[config.urlsKey].splice(fileIndex, 1)
        stages[stageIndex] = stage
        this.updateModelStages({
          client: this.currentClient,
          stages: stages
        }).then(() => {
          console.log('File deleted.')
        })
      }
    },
    timestampToDate (timestamp) {
      const date = timestamp.toDate()
      const options = { month: 'short', day: 'numeric' }
      return date.toLocaleDateString('en-US', options)
    },
    addStageFiles (stageIndex, uploadedFiles) {
      let stages = _.cloneDeep(this.currentClientStages)
      let stage = stages[stageIndex]
      uploadedFiles.forEach(uploaded => {
        const config = DELIVERABLE_KINDS[uploaded.kind]
        if (!stage[config.urlsKey]) {
          stage[config.urlsKey] = []
        }
        if (!stage[config.datesKey]) {
          stage[config.datesKey] = {}
        }
        stage[config.urlsKey].push(uploaded.url)
        if (!stage[config.namesKey]) {
          stage[config.namesKey] = {}
        }
        stage[config.datesKey][uploaded.url] = new Date()
        stage[config.namesKey][uploaded.url] = uploaded.name
      })
      stages[stageIndex] = stage
      this.updateModelStages({
        client: this.currentClient,
        stages: stages
      }).then(() => {
        console.log('Stages updated')
        // Log the file upload activity
        this.logStageFileUpload({
          modelId: this.currentClient,
          stageName: stage.name
        }).then(() => {
          console.log('File upload activity logged')
        }).catch(error => {
          console.error('Error logging file upload activity:', error)
        })
      })
    },
    toggleStageCompleted (stageIndex) {
      const index = _.findIndex(this.currentClientObject.stages, { name: this.enabledStages[stageIndex].name })
      console.log(index)
      this.stages[index].dateCompleted = -1
      this.updateModelStageStatus({
        modelId: this.currentClient,
        stageIndex: index
      })
    },
    selectFile: function (stageName) {
      this.currentStage = _.findIndex(this.currentClientObject.stages, { name: stageName })
      this.$refs.file.click()
    },
    // Content-Disposition with an ASCII fallback and the UTF-8 name for accents in model or stage names
    attachmentDisposition: function (fileName) {
      const asciiName = fileName.replace(/[^\x20-\x7E]/g, '_').replace(/"/g, "'")
      return `attachment; filename="${asciiName}"; filename*=UTF-8''${encodeURIComponent(fileName)}`
    },
    // Picked files with spreadsheets go to the assignment dialog first, pre-assigned only where their
    // names settle it; reports alone upload straight away
    uploadSelectedFiles: function () {
      const files = Array.from(this.$refs.file.files)
      // Cleared so that picking the same file again still triggers a change
      this.$refs.file.value = ''
      let pending = []
      let skipped = []
      files.forEach(file => {
        const match = classifyDeliverable(file)
        if (match) {
          pending.push({ file: file, kinds: match.kinds, kind: null, extension: match.extension })
        } else {
          skipped.push(file.name)
        }
      })
      const spreadsheetCount = pending.filter(isSpreadsheetItem).length
      if (spreadsheetCount > MAX_SPREADSHEETS_PER_UPLOAD) {
        alert(this.$t('views.ModelingProcess.tooManySpreadsheets', { max: MAX_SPREADSHEETS_PER_UPLOAD }))
        return
      }
      // Without spreadsheets there is nothing to assign: skipped files are reported and the rest upload
      if (spreadsheetCount === 0) {
        if (skipped.length > 0) {
          alert(this.$t('views.ModelingProcess.unsupportedFiles', { files: skipped.join(', ') }))
        }
        if (pending.length > 0) {
          this.uploadFiles(this.currentStage, preassignDeliverables(pending))
        }
        return
      }
      this.pendingUploads = preassignDeliverables(pending)
      this.skippedFiles = skipped
      this.assignDialog = true
    },
    confirmUpload: function () {
      if (!this.allAssigned) {
        return
      }
      this.assignDialog = false
      this.uploadFiles(this.currentStage, this.pendingUploads)
    },
    uploadFiles: async function (stageIndex, toUpload) {
      const stage = this.currentClientStages[stageIndex]
      // Versions are numbered per deliverable line, counting the ones this batch adds
      let nextVersion = {}
      Object.keys(DELIVERABLE_KINDS).forEach(kind => {
        nextVersion[kind] = (stage[DELIVERABLE_KINDS[kind].urlsKey] || []).length + 1
      })
      let uploadedFiles = []
      this.uploadCancelled = false
      this.uploadDialog = true
      for (let i = 0; i < toUpload.length && !this.uploadCancelled; i++) {
        const item = toUpload[i]
        const path = `/deliverables/${this.currentClient}/${this.currentClientObject.name}-${stage.name}_${window.crypto.getRandomValues(new Uint32Array(1))}.${item.extension}`
        const contentDisposition = item.kind !== 'report'
          ? this.attachmentDisposition(`${this.deliverableName(stage.name, nextVersion[item.kind], item.kind)}.${item.extension}`)
          : null
        this.uploadCount = { current: i + 1, total: toUpload.length }
        try {
          const uploaded = await this.triggerUploadFile(item.file, path, contentDisposition)
          // Cancelling before the first progress report can't stop the upload, but it isn't registered
          if (!uploaded || this.uploadCancelled) {
            break
          }
          const url = await this.getFileDownloadUrl({ path: path })
          uploadedFiles.push({ kind: item.kind, url: url, name: item.file.name })
          nextVersion[item.kind]++
        } catch (error) {
          // The error was already shown; the files uploaded before it are still registered
          console.error(error)
          break
        }
      }
      this.uploadDialog = false
      this.uploadCount = null
      if (uploadedFiles.length > 0) {
        this.addStageFiles(stageIndex, uploadedFiles)
      }
    },
    triggerUploadFile: function (file, fileName, contentDisposition) {
      return new Promise((resolve, reject) => {
        // Start the bar at 0% instead of where the previous upload left it
        this.resetUploadFileProgress()
        this.currentFile = file
        this.uploadFile({
          file: file,
          path: fileName,
          contentDisposition: contentDisposition,
          customMetadata: {
            'uploadedBy': this.authUser.email,
            'originalName': file.name
          }
        }).then(() => {
          resolve(true)
        }, error => {
          if (error && error.code === 'storage/canceled') {
            resolve(false)
            return
          }
          alert(this.$t('views.ModelingProcess.errorUploadingFile'))
          reject(error)
        }).finally(() => {
          this.currentFile = null
        })
      })
    },
    cancelUpload: function () {
      // The task only exists once the upload reports its first progress
      if (this.uploadFileProgress && this.uploadFileProgress.task) {
        this.uploadFileProgress.task.cancel()
      }
      this.uploadCancelled = true
      this.uploadDialog = false
    },
    fileSizeToString: function (size) {
      return numbro(size).format({
        output: 'byte',
        base: 'binary',
        mantissa: 1
      })
    }
  },
  computed: {
    ...mapGetters('auth', [
      'authUser'
    ]),
    ...mapGetters('storage', [
      'uploadFileProgress'
    ]),
    ...mapGetters('clients', [
      'currentClient',
      'currentClientObject',
      'currentClientStages',
      'currentClientStageFeedback'
    ]),
    feedbackByStage () {
      return _.groupBy(this.currentClientStageFeedback, 'stageName')
    },
    // Who has written in each stage's chat, in order of arrival, with their first and last message.
    // Deleted messages count too: they stay in the chat as deleted
    participantsByStage () {
      return _.mapValues(this.feedbackByStage, messages => {
        let participants = []
        let byUid = {}
        messages.forEach(message => {
          let participant = byUid[message.createdBy]
          if (!participant) {
            participant = { uid: message.createdBy, name: message.createdByName, isStaff: !!message.isStaff, first: message.createdAt, count: 0 }
            byUid[message.createdBy] = participant
            participants.push(participant)
          }
          participant.last = message.createdAt
          participant.count++
        })
        return participants
      })
    },
    acceptedExtensions () {
      return _.flatMap(Object.values(DELIVERABLE_KINDS), config => config.extensions.map(extension => `.${extension}`)).join(',')
    },
    assignStageName () {
      const stage = this.currentClientStages && this.currentClientStages[this.currentStage]
      return stage ? stage.name : ''
    },
    allAssigned () {
      return this.pendingUploads.every(item => !!item.kind)
    },
    // Kinds given more than one file: they become consecutive versions, which is rarely what's meant
    repeatedKindLabels () {
      // Unassigned files aren't a kind yet
      const counts = _.countBy(this.pendingUploads.filter(item => !!item.kind), 'kind')
      return Object.keys(counts).filter(kind => counts[kind] > 1).map(kind => this.kindLabel(kind))
    },
    feedbackReviewStage () {
      return this.feedbackStageName ? _.find(this.stages, { name: this.feedbackStageName }) || null : null
    },
    enabledStages () {
      return this.stages.filter(stage => this.currentClientObject.enabledStages && this.currentClientObject.enabledStages[stage.name])
    }
  },
  components: {
    StageFeedbackDialog
  },
  created () {
    this.loadClientStages({
      client: this.currentClient
    })
    this.loadClientStageFeedback({
      client: this.currentClient
    })
  },
  watch: {
    currentClientStages: {
      immediate: true,
      handler () {
        if (this.currentClientStages) {
          this.stages = this.currentClientStages
        } else {
          console.log('No stages defined for client')
        }
      }
    }
  }
}
